import React, { createContext, useContext, useState, useEffect } from 'react';

const PlayerComparisonContext = createContext();

export const usePlayerComparison = () => {
  const context = useContext(PlayerComparisonContext);
  if (!context) {
    throw new Error('usePlayerComparison must be used within a PlayerComparisonProvider');
  }
  return context;
};

export const PlayerComparisonProvider = ({ children, filteredPlayers = [] }) => {
  const [compareMode, setCompareMode] = useState(false);
  const [playersToCompare, setPlayersToCompare] = useState([]);
  const [originalPlayers, setOriginalPlayers] = useState(filteredPlayers);

  // Update original players when filteredPlayers changes
  useEffect(() => {
    setOriginalPlayers(filteredPlayers);
  }, [filteredPlayers]);

  // Get the effective players list based on compareMode
  const getEffectivePlayers = () => {
    return compareMode ? playersToCompare : originalPlayers;
  };

  // Add or remove a player from comparison
  const togglePlayerComparison = (player) => {
    const exists = playersToCompare.some((p) => p.id === player.id);
    if (exists) {
      setPlayersToCompare(playersToCompare.filter((p) => p.id !== player.id));
    } else {
      setPlayersToCompare([...playersToCompare, player]);
    }
  };

  // Check if a player is selected for comparison
  const isPlayerSelected = (playerId) => {
    return playersToCompare.some((p) => p.id === playerId);
  };

  // Enter comparison mode with selected players
  const enterCompareMode = () => {
    if (playersToCompare.length >= 2) {
      setCompareMode(true);
    }
  };

  // Exit comparison mode and reset
  const exitCompareMode = () => {
    setCompareMode(false);
    // Keep the selected players so user can re-enter comparison mode
  };

  // Clear all selected players
  const clearComparison = () => {
    setPlayersToCompare([]);
    setCompareMode(false);
  };

  // Set players to compare directly (useful for bulk selection)
  const setComparisonPlayers = (players) => {
    setPlayersToCompare(players);
  };

  const value = {
    compareMode,
    playersToCompare,
    originalPlayers,
    effectivePlayers: getEffectivePlayers(),
    togglePlayerComparison,
    isPlayerSelected,
    enterCompareMode,
    exitCompareMode,
    clearComparison,
    setComparisonPlayers,
    canCompare: playersToCompare.length >= 2
  };

  return (
    <PlayerComparisonContext.Provider value={value}>
      {children}
    </PlayerComparisonContext.Provider>
  );
};